import React from "react";
import { DESK_STAGE, DOT, PHASE_KEYS, STEP_PARENT, STEP_STAGE } from "@/lib/stages";
import { StageBox, StageHover } from "../crm/StagePill";
import { I } from "./icons";
import { useFixedMenu } from "./useFixedMenu";

/* Core stages only — what is happening inside Under assessment is a Right now update (NowPicker), never a stage. */
const OPTIONS = [
  { key: "in_review", ...DESK_STAGE.in_review, note: "you're assessing it" },
  { key: "awaiting_docs", ...DESK_STAGE.awaiting_docs, note: "automatic while documents are requested", auto: true,
    hint: "Ask for documents on the case and it moves here on its own" },
  { key: "preparing_offer", ...DESK_STAGE.preparing_offer, note: "every document is in", gated: true },
  { key: "offer_made", ...DESK_STAGE.offer_made, hint: "Asks for your terms — send new ones any time" },
  { ...STEP_STAGE.offer_accepted, needs: "offer_made" },
  { key: "offer_declined", label: "Client turned it down", note: "stays Offer issued", tone: "mint",
    needs: "offer_made", hint: "You can still send new terms, or pass" },
  { key: "completed", ...DESK_STAGE.completed },
  { key: "declined", ...DESK_STAGE.declined, note: "pass on the case", hint: "Asks why" },
];

const MENU_H = 400;
const MENU_W = 248;

/**
 * The stage changer — a `.sel` pill in the row that drops a `.menu`. One click, no page reload.
 * "With lenders" is where a case starts, so it is shown but never offered: a desk moves a case forward.
 * Offer accepted and the client's turn-down only open once the desk's own offer is on the case.
 */
export function StagePicker({ value, state, onPick, disabled, testId, docs = 0 }) {
  const m = useFixedMenu(MENU_H, MENU_W);
  const core = PHASE_KEYS.includes(state?.key) ? state.key : STEP_PARENT[value] === "in_review" ? "in_review" : value;
  const hasOffer = ["offer_made", "offer_accepted", "offer_declined"].includes(value);
  return (
    <div ref={m.box} style={{ position: "relative" }}>
      <StageHover state={state} testId={testId} tap={false}>
        <button className="sel" onClick={(e) => { if (!disabled) m.toggle(e); }} disabled={disabled} aria-expanded={m.open}
                style={disabled ? { opacity: 0.6, cursor: "not-allowed" } : undefined} data-testid={testId} data-stage={state?.key}>
          <StageBox state={state?.label ? state : { ...state, label: "Set a stage", ring: "sent", tone: "ink" }} />
          <I.chevron />
        </button>
      </StageHover>
      {m.open && (
        <div className="menu sp-menu" style={{ position: "fixed", ...m.pos, width: MENU_W, maxHeight: "calc(100vh - 16px)", overflowY: "auto" }}
             data-testid={testId ? `${testId}-menu` : undefined}>
          {OPTIONS.map((o) => {
            const locked = (o.needs && !hasOffer) || o.auto || (o.gated && (docs > 0 || hasOffer || value === "completed"));
            const why = o.auto ? o.hint : o.gated && docs > 0
              ? `${docs} document request${docs === 1 ? " is" : "s are"} still open — this opens once everything is in`
              : o.gated && locked ? "Preparing offer comes before the offer" : locked ? "Make your offer first — then mark how the client answered" : o.hint;
            return (
              <button key={o.key} className={core === o.key ? "sel-on" : undefined} disabled={locked && core !== o.key}
                      title={why}
                      style={locked ? { opacity: 0.45, cursor: "not-allowed" } : undefined}
                      onClick={() => { if (locked) return; m.setOpen(false); if (core !== o.key) onPick(o.key); }}
                      data-testid={testId ? `${testId}-${o.key}` : undefined}>
                <i className="sq" style={{ background: DOT[o.tone] }} />
                <span>{o.label}{(o.gated && docs > 0 ? `${docs} document${docs === 1 ? "" : "s"} still to come` : o.note) &&
                  <small className="m-note">{o.gated && docs > 0 ? `${docs} document${docs === 1 ? "" : "s"} still to come` : o.note}</small>}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default StagePicker;
